import { useState } from "react";

import { DashboardScreenBreakdownDialog } from "@/components/dashboard/dashboard-stats/DashboardScreenBreakdownDialog";
import { DashboardTeamGoalsCard } from "@/components/dashboard/dashboard-stats/main-stats/DashboardTeamGoalsCard";
import { DashboardGoalsOverTimeCard } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardGoalsOverTimeCard";
import { DashboardIndividualOtherCard } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardIndividualOtherCard";

import {
  DASHBOARD_GRID_FUNNEL_COLUMN_CLASS,
  DASHBOARD_GRID_GOALS_COLUMN_CLASS,
  DASHBOARD_GRID_STATS_COLUMN_CLASS,
} from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  GoalPeriod,
} from "@/types";

// One staffer: their five weeks, plus their submit splits. The screen
// breakdown lives here because only this view can open it — the endpoint is
// per staffer.
export const DashboardStatRowIndividualView = ({
  comparisonMetric,
  goalsPeriod,
  isPending,
  itemClassName,
  onGoalsPeriodChange,
  onMetricChange,
  overview,
  selectedUserId,
}: {
  comparisonMetric: DashboardComparisonMetric;
  goalsPeriod: GoalPeriod;
  isPending: boolean;
  itemClassName: string;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  onMetricChange: (metric: DashboardComparisonMetric) => void;
  overview: DashboardKpiOverview | undefined;
  selectedUserId: number;
}) => {
  /* React Hooks */
  // Opened either from a week on the trend or from the time-to-screen number
  // itself. A null weekStart means the whole period.
  const [breakdown, setBreakdown] = useState<{
    weekStart: string | null;
  } | null>(null);

  /* Helpers */
  const periodData = dashboardKpis.periodFor(overview, goalsPeriod);

  // The rule inside the dialog shows the same median the card it came from did.
  const breakdownMedianHours = (() => {
    if (!breakdown) return null;
    if (breakdown.weekStart === null) {
      return periodData ? periodData.totals.medianHoursToFirstScreen : null;
    }
    const week = overview?.trend.find(
      (row) => row.weekStart === breakdown.weekStart,
    );
    return week ? week.medianHoursToFirstScreen : null;
  })();

  const breakdownSubtitle = (() => {
    if (!breakdown) return "";
    if (breakdown.weekStart !== null) {
      return `Week of ${dates.formatShortDate(breakdown.weekStart)}`;
    }
    if (!periodData) return "";
    const start = new Date(periodData.periodStart);
    return goalsPeriod === GoalPeriod.WEEK
      ? `Week of ${dates.formatShortDate(periodData.periodStart)}`
      : dates.formatMonthYear(start.getMonth() + 1, start.getFullYear());
  })();

  /* Handlers */
  const handleOpenPeriodBreakdown = () => {
    setBreakdown({ weekStart: null });
  };

  const handleOpenWeekBreakdown = (weekStart: string) => {
    setBreakdown({ weekStart });
  };

  const handleCloseBreakdown = () => {
    setBreakdown(null);
  };

  /* Render */
  return (
    <>
      <div className={cn(itemClassName, DASHBOARD_GRID_GOALS_COLUMN_CLASS)}>
        <DashboardTeamGoalsCard
          goalsPeriod={goalsPeriod}
          isPending={isPending}
          onGoalsPeriodChange={onGoalsPeriodChange}
          onOpenScreenBreakdown={handleOpenPeriodBreakdown}
          onSelectComparisonMetric={onMetricChange}
          overview={overview}
          selectedComparisonMetric={comparisonMetric}
          selectedUserId={selectedUserId}
        />
      </div>
      <div className={cn(itemClassName, DASHBOARD_GRID_FUNNEL_COLUMN_CLASS)}>
        <DashboardGoalsOverTimeCard
          goalsPeriod={goalsPeriod}
          isPending={isPending}
          metric={comparisonMetric}
          onMetricChange={onMetricChange}
          onOpenScreenBreakdown={handleOpenWeekBreakdown}
          overview={overview}
        />
      </div>
      <div className={cn(itemClassName, DASHBOARD_GRID_STATS_COLUMN_CLASS)}>
        {/* Remounts per staffer so the card reopens on Search submits. */}
        <DashboardIndividualOtherCard
          key={selectedUserId}
          isPending={isPending}
          overview={overview}
          selectedUserId={selectedUserId}
        />
      </div>
      <DashboardScreenBreakdownDialog
        isOpen={breakdown !== null}
        medianHours={breakdownMedianHours}
        onClose={handleCloseBreakdown}
        period={goalsPeriod}
        subtitle={breakdownSubtitle}
        userId={selectedUserId}
        weekStart={breakdown ? breakdown.weekStart : null}
      />
    </>
  );
};
